import { useQuery } from '@apollo/client';
import React, { useRef, useState } from 'react';
import ControlBarContributorsButton from './ControlBarContributorsButton';
import ControlBarInsights from './ControlBarInsights';
import * as styles from './ControlBarInsightsButton.module.scss';
import ControlBarPopOver from './ControlBarPopOver';
import LoadingIndicator from '../components/LoadingIndicator';
import { gql } from '../graphql/generated';
import useIsLayoutSize from './utils/useIsLayoutSize';
import { LayoutSize } from './utils/useLayoutSize';
import type { DocumentType } from '../graphql/generated/gql';

const POST_FRAGMENT = gql(`
  fragment ControlBarInsightsButton_post on Post {
    id
    ...ControlBarContributorsButton_post
  }
`);

const POST_QUERY = gql(/** @complexity 20 */ `
  query ControlBarInsightsButtonPost($id: ID!) {
    post(id: $id) {
      id
      ...ControlBarInsights_post
    }
  }
`);

interface ControlBarInsightsButtonProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  children: React.ReactNode;
  popOverPlacement: React.ComponentProps<typeof ControlBarPopOver>['placement'];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ControlBarInsightsButton = ({
  post,
  children,
  popOverPlacement,
  open,
  onOpenChange,
}: ControlBarInsightsButtonProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const [shouldLoadPopOverData, setShouldLoadPopOverData] = useState(false);

  const { data } = useQuery(POST_QUERY, {
    skip: !shouldLoadPopOverData && !open,
    variables: { id: post.id },
  });

  if (isMobile) {
    return (
      <ControlBarContributorsButton post={post} interactionsDisabled>
        {children}
      </ControlBarContributorsButton>
    );
  }

  return (
    <ControlBarPopOver
      onOpenChange={onOpenChange}
      anchorRef={anchorRef}
      content={
        <div className={styles.contentContainer}>
          {data?.post ? (
            <ControlBarInsights
              post={data.post}
              close={() => onOpenChange(false)}
            />
          ) : (
            <div className={styles.loading}>
              <LoadingIndicator />
            </div>
          )}
        </div>
      }
      placement={popOverPlacement}
      open={open}
    >
      <ControlBarContributorsButton
        post={post}
        active={open}
        avatarStackRef={anchorRef}
        onPointerDown={() => {
          setShouldLoadPopOverData(true);
        }}
      >
        {children}
      </ControlBarContributorsButton>
    </ControlBarPopOver>
  );
};

export default ControlBarInsightsButton;
